import { Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { clampQuantity, MAX_LINE_QUANTITY } from "../cart.model";

export interface QuantityStepperProps {
  value: number;
  onChange: (quantity: number) => void;
  /** El mínimo. `0` permite vaciar la línea desde el "−". */
  min?: number;
  max?: number;
  /** Nombre del producto: entra en los rótulos accesibles de los tres controles. */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * El selector de cantidad: "−", un número escribible y "+".
 *
 * ── Por qué el número es un `<input>` de verdad ──────────────────────
 * Un display de solo lectura entre dos botones obliga a pulsar once veces
 * para pedir doce frascos. Con un campo, se escribe. Lleva
 * `inputMode="numeric"` para que en un teléfono salga el teclado de
 * números, y `appearance-none` para quitar las flechitas nativas, que
 * duplicarían los dos botones que ya están ahí.
 *
 * ── Lo que se acota, y cuándo ────────────────────────────────────────
 * Mientras se escribe NO se acota a `min`: si alguien borra el campo para
 * teclear "12", acotar a cada pulsación lo devolvería a "1" y el
 * resultado sería "112". El campo vacío se deja vacío y es al SALIR
 * (`onBlur`) cuando se resuelve. Al máximo sí se acota en el momento,
 * porque ahí no hay estado intermedio legítimo.
 *
 * ── Y por qué el "+" se apaga en el tope ─────────────────────────────
 * Un botón que se puede pulsar y no hace nada se lee como una avería. Con
 * `disabled`, además, el lector de pantalla lo anuncia.
 */
export function QuantityStepper({
  value,
  onChange,
  min = 1,
  max = MAX_LINE_QUANTITY,
  label,
  disabled = false,
  className,
}: QuantityStepperProps) {
  const { t } = useTranslation();

  const commit = (next: number) => onChange(clampQuantity(next, max));

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-ds-pill border border-ds-border bg-white p-1",
        className,
      )}
    >
      <Button
        variant="secondary"
        size="sm"
        iconOnly
        leftIcon={<Minus />}
        disabled={disabled || value <= min}
        onClick={() => commit(value - 1)}
        aria-label={t("Quitar uno de {{producto}}", { producto: label })}
        className="border-0"
      />

      <input
        type="text"
        inputMode="numeric"
        value={value === 0 ? "" : String(value)}
        disabled={disabled}
        aria-label={t("Cantidad de {{producto}}", { producto: label })}
        onChange={(event) => {
          const digits = event.target.value.replace(/[^0-9]/g, "");
          // Sin `min` aquí a propósito: el campo vacío es un paso
          // intermedio legítimo mientras se reescribe la cantidad.
          onChange(clampQuantity(digits === "" ? 0 : Number(digits), max));
        }}
        onBlur={() => {
          if (value < min) commit(min);
        }}
        className={cn(
          "w-10 border-0 bg-transparent p-0 text-center text-[13px] font-semibold text-ds-ink",
          "outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none",
          "focus-visible:rounded-ds-sm focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50",
          "disabled:opacity-50",
        )}
      />

      <Button
        variant="secondary"
        size="sm"
        iconOnly
        leftIcon={<Plus />}
        disabled={disabled || value >= max}
        onClick={() => commit(value + 1)}
        aria-label={t("Añadir uno de {{producto}}", { producto: label })}
        className="border-0"
      />
    </div>
  );
}
